/* MidiInput — the one owner of "connect a MIDI instrument to this page".
 *
 *   var midi = MidiInput.session({
 *       slug: 'virtual-drums',
 *       device: T.midi_device,             // what to plug in, as the tool says it ("электронные барабаны")
 *       onMessage: function (m, port) {},  // m = MidiInput.parse(data): {type, ch, note, vel, cc, value}
 *       onState: function (st, change) {}  // st = {status, inputs, names, message}; change = {added, removed} | null
 *   });
 *   midi.connect();   // from a click: the browser may ask for permission
 *   midi.reconnect(); // on page load: connects ONLY when permission is already granted, never prompts
 *
 * Statuses (st.status) — each one has its own sentence, because each one has its
 * own way out:
 *   on          inputs are open and listened to
 *   none        access granted, nothing plugged in (hot-plug keeps watching)
 *   busy        the port is listed but another program holds it (Windows MIDI is exclusive: a DAW)
 *   denied      the site is blocked in the browser's permissions — only the person can lift it
 *   dismissed   the prompt was closed without an answer — asking again works
 *   addon       Firefox: access goes through a «site permission add-on» the person declined
 *   blocked     the window forbids it (in-app browser, iframe, insecure page)
 *   apple       iPhone, iPad, Safari: no Web MIDI at all
 *   unsupported any other browser without Web MIDI
 *   error       anything else
 *
 * Why one owner (2026-10-30): six instruments carried a copy of the same
 * `requestMIDIAccess().then(...)`, and none of them could tell these apart.
 * /virtual-drums answered «MIDI недоступен» to a closed prompt, to a blocked site
 * and to an iPhone alike, and counted the person's own «Block» as a tool error —
 * 150 sessions in 90 days, while two sessions in 30 days ever played through
 * MIDI. Comment #1882 («А можно подключить барабаны?») came from one of them.
 * Guards: tests/js/midi-input.test.mjs, MidiInputHasOneOwnerTest.
 * Why-doc: docs/why/midi-input.md. */
(function (root, factory) {
    var api = factory(root);
    if (typeof module === 'object' && module.exports) module.exports = api;
    if (root && root.document && (!root.MidiInput || root.MidiInput.__isStub)) root.MidiInput = api;
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
    'use strict';

    /* English stands in only when the loader did not hand over the page's own
       words (a page that loads the file by hand, or a test). */
    var EN = {
        on: 'Connected: :names',
        none: 'The browser sees no MIDI device. Connect :device with a USB cable and switch it on — it will be picked up right away, no reload needed.',
        busy: ':names is connected, but another program is using it (a DAW or the module\'s editor). Close that program and press the button again.',
        denied: 'MIDI access is blocked for this site. Click the icon to the left of the address, open «MIDI devices», choose «Allow» and press the button again.',
        dismissed: 'The browser asked for MIDI permission but got no answer. Press the button again and choose «Allow».',
        addon: 'Firefox connects MIDI through a small «site permission add-on». Press the button again and accept it — or open this page in Chrome or Edge.',
        blocked: 'This window does not give websites MIDI access — it happens in an app\'s built-in browser. Open this page in Chrome or Edge.',
        apple: 'On iPhone and iPad, and in Safari, websites cannot reach MIDI devices: Apple has not enabled Web MIDI. Connect :device to a computer or an Android device and open this page in Chrome or Edge.',
        unsupported: 'This browser does not work with MIDI devices. Open this page in Chrome or Edge.',
        error: 'Could not connect to MIDI. Reconnect the cable and press the button again.',
        device: 'a MIDI device',
        added: 'Connected: :name',
        removed: 'Disconnected: :name',
        not_loaded: 'The MIDI module failed to load — please refresh the page.',
        short_on: 'MIDI: :names',
        short_none: 'MIDI: no device found',
        short_busy: 'MIDI: device in use',
        short_denied: 'MIDI: no access',
        short_unavailable: 'MIDI not available'
    };

    function strings() {
        var t = (root && root.__MIDI_INPUT_T) || null;
        return t && typeof t === 'object' ? t : EN;
    }
    function say(key, rep) {
        var t = strings();
        var s = (t[key] != null ? t[key] : EN[key]) || '';
        rep = rep || {};
        return String(s).replace(/:([a-z_]+)/g, function (m, k) { return rep[k] != null ? String(rep[k]) : m; });
    }

    function nav() { return (root && root.navigator) || null; }

    /* iOS and iPadOS browsers are all WebKit, and WebKit has no Web MIDI; desktop
       Safari neither. An iPad asks for the desktop site and calls itself
       «Macintosh», so it is told apart by its touch points. */
    function isApple() {
        var n = nav();
        if (!n) return false;
        var ua = String(n.userAgent || '');
        if (/iPad|iPhone|iPod/.test(ua)) return true;
        if (/Macintosh/.test(ua) && (n.maxTouchPoints || 0) > 1) return true;
        return /Safari\//.test(ua) && !/(Chrome|Chromium|CriOS|FxiOS|Edg|OPR|YaBrowser|Firefox)\//.test(ua);
    }

    /* 'ok' | 'apple' | 'insecure' | 'none' — before anything is asked. */
    function support() {
        var n = nav();
        if (n && typeof n.requestMIDIAccess === 'function') {
            if (root && root.isSecureContext === false) return 'insecure';
            return 'ok';
        }
        return isApple() ? 'apple' : 'none';
    }

    /* 'granted' | 'prompt' | 'denied' | 'unknown' — the state that actually GATES
       access. Current Chromium puts every Web MIDI request, sysex or not, behind
       the sysex permission: measured 30.10.2026, `midi` granted + `midi-sysex`
       denied → `requestMIDIAccess({sysex:false})` is refused, while the query for
       {sysex:false} says «granted». Reading that one made reconnect() reach for a
       door that was shut and paint «access blocked» on a page nobody had touched.
       So the sysex state answers; the plain one only where the sysex query is not
       understood. Firefox knows neither name and throws — 'unknown', not a refusal. */
    function query(desc) {
        var n = nav();
        try {
            if (!n || !n.permissions || typeof n.permissions.query !== 'function') return Promise.resolve(null);
            return Promise.resolve(n.permissions.query(desc))
                .then(function (s) { return (s && s.state) || null; }, function () { return null; });
        } catch (_) { return Promise.resolve(null); }
    }
    function permission() {
        return query({ name: 'midi', sysex: true }).then(function (s) {
            return s || query({ name: 'midi', sysex: false }).then(function (p) { return p || 'unknown'; });
        });
    }

    /* A raw MIDI message → what it means. Channel is 0-based. */
    function parse(data) {
        var d = data || [];
        var st = d[0] | 0, hi = st & 0xF0, ch = st & 0x0F;
        var a = d[1] | 0, b = d[2] | 0;
        if (st < 0x80) return { type: 'data' };
        switch (hi) {
            case 0x90: return { type: b > 0 ? 'on' : 'off', ch: ch, note: a, vel: b };
            case 0x80: return { type: 'off', ch: ch, note: a, vel: b };
            case 0xA0: return { type: 'poly', ch: ch, note: a, value: b };
            case 0xB0: return { type: 'cc', ch: ch, cc: a, value: b };
            case 0xC0: return { type: 'program', ch: ch, value: a };
            case 0xD0: return { type: 'pressure', ch: ch, value: a };
            case 0xE0: return { type: 'bend', ch: ch, value: ((b << 7) | a) - 8192 };
            default: return { type: 'system', status: st };
        }
    }

    /* Which way out the failure has. `perm` is the permission state read AFTER
       the refusal: Chromium words a closed prompt and a blocked site the same
       («Permission to use Web MIDI API was not granted»), the state does not. */
    function classify(err, perm) {
        var name = (err && err.name) || '';
        var msg = String((err && err.message) || err || '');
        if (/site permission add-?on/i.test(msg)) return 'addon';
        if (/current context|not allowed by the user agent|permissions? policy|feature policy|secure context/i.test(msg)) return 'blocked';
        if (perm === 'denied') return 'denied';
        if (perm === 'prompt') return 'dismissed';
        if (name === 'NotAllowedError' || name === 'SecurityError' || /not granted|denied|permission/i.test(msg)) return 'denied';
        if (name === 'NotSupportedError') return 'unsupported';
        return 'error';
    }

    function tel(type, payload) {
        try { if (root && typeof root.__ae === 'function') root.__ae(type, payload); } catch (_) {}
    }
    function toolError(payload) {
        try { if (root && typeof root.__aeToolError === 'function') root.__aeToolError(payload); } catch (_) {}
    }
    function clip(s, n) { s = String(s == null ? '' : s); return s.length > n ? s.slice(0, n - 1) + '…' : s; }
    function later(fn) { setTimeout(fn, 0); }

    function session(opts) {
        opts = opts || {};
        var slug = opts.slug || '';
        var device = opts.device || say('device');
        var access = null, pending = null, closed = false;
        var bound = {};        /* port id → port we listen to */
        var busy = {};         /* port id → true: another program holds it */
        var told = false;      /* the first answer is always handed to onState */
        var self = {
            state: { status: 'idle', inputs: [], names: '', message: '' },
            connect: function () { return open('gesture'); },
            reconnect: function () {
                if (closed || support() !== 'ok') return Promise.resolve(null);
                return permission().then(function (p) { return p === 'granted' ? open('reconnect') : null; });
            },
            close: close
        };

        function onMsg(e) {
            if (closed || !opts.onMessage) return;
            var m = parse(e && e.data);
            /* clock and active sensing come several times a second from many modules */
            if (m.type === 'system' || m.type === 'data') return;
            /* How long the message waited for the page: its timeStamp is when the
               browser received it, in performance.now() time. A busy main thread
               hands a hit over late; an instrument that records or scores can put it
               back where the player struck (lagMs), the sound still plays at once. */
            m.lagMs = 0;
            try {
                var ts = e && e.timeStamp, now = root.performance && root.performance.now();
                if (typeof ts === 'number' && ts > 0 && typeof now === 'number' && now >= ts) m.lagMs = Math.min(now - ts, 1000);
            } catch (_) {}
            /* the raw event rides along: an instrument with its own tested byte
               handling keeps it, the connection is all it hands over */
            try { opts.onMessage(m, (e && (e.currentTarget || e.target)) || null, e); } catch (err) { later(function () { throw err; }); }
        }

        function listed() {
            var out = [];
            try {
                if (access && access.inputs) access.inputs.forEach(function (p) {
                    if (p && p.state !== 'disconnected') out.push(p);
                });
            } catch (_) {}
            return out;
        }

        function bind(p) {
            if (bound[p.id] === p) return;
            bound[p.id] = p;
            try { p.onmidimessage = onMsg; } catch (_) {}
            /* Setting onmidimessage opens the port implicitly, and an implicit open
               that fails says nothing. Open it explicitly to hear the refusal: on
               Windows a port another program holds rejects here. */
            try {
                if (typeof p.open === 'function') {
                    Promise.resolve(p.open()).then(function () {
                        if (busy[p.id]) { delete busy[p.id]; publish(); }
                    }, function () {
                        if (!busy[p.id]) { busy[p.id] = true; publish(); }
                    });
                }
            } catch (_) {}
        }

        /* `asked`: a press on «Connect» deserves an answer even when nothing changed
           (already connected, still nothing plugged in) — the tool showed «connecting…».
           `quiet`: reconnect() on page load shows itself only when a device IS there. A
           person who once allowed MIDI and has nothing plugged in would otherwise meet
           «no device found» on every visit — a state nobody asked about. A device
           plugged in later is still told (hot-plug), a press still gets the sentence. */
        function publish(asked, quiet) {
            if (closed) return self.state;
            var ports = listed();
            ports.forEach(bind);
            Object.keys(bound).forEach(function (id) {
                if (!ports.some(function (p) { return p.id === id; })) {
                    try { bound[id].onmidimessage = null; } catch (_) {}
                    delete bound[id]; delete busy[id];
                }
            });
            var inputs = ports.map(function (p) {
                return { id: p.id, name: String(p.name || p.manufacturer || 'MIDI'), manufacturer: String(p.manufacturer || ''), busy: !!busy[p.id] };
            });
            var live = inputs.filter(function (i) { return !i.busy; });
            var status = !inputs.length ? 'none' : (live.length ? 'on' : 'busy');
            var names = (status === 'busy' ? inputs : live).map(function (i) { return i.name; }).join(', ');
            var st = { status: status, inputs: inputs, names: names, message: say(status, { names: names, device: device }) };
            var was = self.state;
            self.state = st;
            var change = null;
            if (told) {
                var before = was.inputs || [];
                var added = inputs.filter(function (i) { return !before.some(function (b) { return b.id === i.id; }); }).map(function (i) { return i.name; });
                var removed = before.filter(function (b) { return !inputs.some(function (i) { return i.id === b.id; }); }).map(function (b) { return b.name; });
                if (added.length || removed.length) change = { added: added, removed: removed };
                if (added.length) tel('feature_use', { slug: slug, f: 'midi_device', act: 'added', names: clip(added.join('|'), 80) });
            }
            if (opts.onState && !(quiet && st.status !== 'on') && (asked || !told || change || st.status !== was.status || st.names !== was.names)) {
                told = true;
                try { opts.onState(st, change); } catch (err) { later(function () { throw err; }); }
            }
            told = true;
            return st;
        }

        function fail(status, err, how, perm) {
            var st = { status: status, inputs: [], names: '', message: say(status, { device: device, names: '' }) };
            self.state = st;
            tel('feature_use', { slug: slug, f: 'midi_connect', result: status, how: how, perm: perm || '' });
            /* Only the unexplained is an error. A refusal is the person's answer and
               an iPhone is the platform: counting them as tool errors is what made
               /virtual-drums read «89% of runs fail». */
            if (status === 'error') toolError({ slug: slug, error_type: 'midi_connect_failed', err: clip(err && err.name ? err.name + ': ' + err.message : err, 90) });
            if (opts.onState) { try { opts.onState(st, null); } catch (e) { later(function () { throw e; }); } }
            return st;
        }

        function open(how) {
            if (closed) return Promise.resolve(self.state);
            if (access) return Promise.resolve(publish(how === 'gesture'));
            if (pending) return pending;
            var sup = support();
            if (sup !== 'ok') {
                return Promise.resolve(fail(sup === 'apple' ? 'apple' : (sup === 'insecure' ? 'blocked' : 'unsupported'), null, how));
            }
            var before = 'unknown';
            pending = permission().then(function (p) {
                before = p;
                return nav().requestMIDIAccess({ sysex: false });
            }).then(function (a) {
                pending = null;
                if (closed) return self.state;
                access = a;
                try { a.onstatechange = function () { publish(); }; } catch (_) {}
                var st = publish(false, how === 'reconnect');
                tel('feature_use', { slug: slug, f: 'midi_connect', result: st.status, how: how, perm: before, devices: st.inputs.length, names: clip(st.inputs.map(function (i) { return i.name; }).join('|'), 80) });
                return st;
            }, function (err) {
                pending = null;
                return permission().then(function (after) {
                    /* reconnect() runs on page load, unasked: a refusal there is not the
                       person's to hear. Counted, not shown — the page stays idle. */
                    if (how === 'reconnect') {
                        tel('feature_use', { slug: slug, f: 'midi_connect', result: classify(err, after), how: how, perm: before + '>' + after });
                        return null;
                    }
                    return fail(classify(err, after), err, how, before + '>' + after);
                });
            });
            return pending;
        }

        function close() {
            closed = true;
            Object.keys(bound).forEach(function (id) { try { bound[id].onmidimessage = null; } catch (_) {} });
            bound = {};
            try { if (access) access.onstatechange = null; } catch (_) {}
        }

        return self;
    }

    /* For an instrument whose MIDI control is one small label and a dot: the short
       word for a state ('' = keep the tool's own «Connect» — a closed prompt or a
       declined add-on is answered by pressing again), and its colour. The full
       sentence with the way out is st.message: show it where there is room, or in
       a toast after the press. */
    function label(st) {
        var s = st && st.status;
        if (s === 'on') return say('short_on', { names: st.names });
        if (s === 'none') return say('short_none');
        if (s === 'busy') return say('short_busy');
        if (s === 'denied') return say('short_denied');
        if (s === 'apple' || s === 'unsupported' || s === 'blocked' || s === 'error') return say('short_unavailable');
        return '';
    }
    function tone(st) {
        var s = st && st.status;
        if (s === 'on') return 'ok';
        if (s === 'none' || s === 'busy' || s === 'dismissed' || s === 'addon') return 'warn';
        if (!s || s === 'idle') return 'idle';
        return 'err';
    }
    /* A device plugged in or pulled out while the page is open, told once. */
    function toastChange(change) {
        if (!change || !root || typeof root.__toast !== 'function') return;
        try {
            if (change.added && change.added.length) root.__toast(say('added', { name: change.added.join(', ') }), 'success');
            if (change.removed && change.removed.length) root.__toast(say('removed', { name: change.removed.join(', ') }), 'info');
        } catch (_) {}
    }
    /* After a press: the outcome, unless it is plainly visible already. */
    function toastOutcome(st) {
        if (!st || st.status === 'on' || st.status === 'idle' || !root || typeof root.__toast !== 'function') return;
        try { root.__toast(st.message, (st.status === 'none' || st.status === 'dismissed' || st.status === 'addon') ? 'info' : 'error'); } catch (_) {}
    }

    return {
        session: session,
        support: support,
        permission: permission,
        parse: parse,
        classify: classify,
        say: say,
        label: label,
        tone: tone,
        toastChange: toastChange,
        toastOutcome: toastOutcome,
        isApple: isApple
    };
});
